import { useModalMeta } from "@/shared/atoms/useModal";
import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook";
import useScreenMutation from "@/shared/mutations/screens/useScreenPostMutation";
import useScreenPutMutation from "@/shared/mutations/screens/useScreenPutMutation";
import useScreensQuery from "@/shared/queries/screens/useScreensQuery";
import { countryLanguages, fontFamilyOptions } from "@/shared/utils/constant";
import { getFieldByLang } from "@/shared/utils/helpers";
import { Screen } from "@/shared/v2/queries/interfacesV2";
import { useEffect } from "react";
import { Col, Row } from "react-grid-system";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import BooleanWithLabel from "../Boolean/BooleanWithLabel";
import Button from "../Button/Button";
import Textfield from "../Input/Textfield";
import Modal, { ModalProps } from "../Modal/Modal";
import Select from "../Select/Select";
import useCountryConfigHook from "@/shared/hooks/useCountryConfigHook";

const ScreenModal = ({ onExit, ...props }: ModalProps) => {
  const { refetch } = useScreensQuery();
  const [meta] = useModalMeta();
  const defaultValues: Screen = meta?.defaultValues;
  const isEdit = defaultValues !== undefined;
  const create = useScreenMutation();
  const edit = useScreenPutMutation();
  const { t } = useTranslation("");
  const { language } = useCountryConfigHook();
  const { register, control, errors, handleSubmit } = useForm({
    defaultValues: isEdit ? {show_price:true,...defaultValues} : { language },
  });
  const query = useCategoryQueryHook();
  const isAppliedOnWholeMenu = useWatch({
    name: "apply_on_menu",
    control,
    defaultValue: true,
  });

  useEffect(() => {
    query.refetch();
  }, []);

  const getPropertyName = (value: boolean) =>
    value ? `not_apply_on_item_ids` : "apply_on_item_ids";

  const onSubmit = (data: Screen) => {
    const record: Screen = {
      ...defaultValues,
      ...data,
      is_active: true,
    };
    if (record.apply_on_item_ids === undefined) {
      record["apply_on_item_ids"] = [];
    }
    if (record.not_apply_on_item_ids === undefined) {
      record["not_apply_on_item_ids"] = [];
    }
    const mutate = isEdit ? edit : create;
    mutate.mutateAsync(record).then(() => {
      refetch();
      onExit();
    });
  };
  const name = getPropertyName(isAppliedOnWholeMenu);

  return (
    <Modal width={{ _: "95%", sm: "60rem" }} onExit={onExit} {...props}>
      <form onSubmit={handleSubmit(onSubmit)} className="pt-[4rem] px-[2rem]">
        <Row>
          <Col xs={12} md={6}>
            <div className="mb-[2rem]">
              <Textfield
                name="name"
                label={t("name")}
                placeholder={t("name")}
                ref={register({
                  required: {
                    value: true,
                    message: t("fieldRequired"),
                  },
                })}
              />
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className="mb-[2rem]">
              <Textfield
                name="description"
                label={t("description")}
                placeholder={t("description")}
                ref={register({
                  required: {
                    value: true,
                    message: t("fieldRequired"),
                  },
                })}
              />
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className="mb-[2rem]">
              <Textfield
                type="number"
                name="minutes_in_advance_for_time_orders"
                label={t("minutes_in_advance_for_time_orders")}
                placeholder={t("beforeShowPlaceholder")}
                ref={register({
                  required: {
                    value: true,
                    message: t("fieldRequired"),
                  },
                })}
              />
            </div>
          </Col>
          <Col xs={12} md={3}>
            <div className="mb-[2rem]">
              <Textfield
                type="number"
                name="font_size"
                label={t("fontSize")}
                placeholder={t("fontSize")}
                ref={register({
                  required: {
                    value: true,
                    message: t("fieldRequired"),
                  },
                })}
              />
            </div>
          </Col>
          <Col xs={12} md={3}>
            <div className="mb-[2rem]">
              <Controller
                defaultValue={fontFamilyOptions[0].value}
                name={"font_family"}
                control={control}
                rules={{
                  required: {
                    value: isAppliedOnWholeMenu === false,
                    message: t("fieldRequired"),
                  },
                }}
                render={(props) => (
                  <Select
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPlacement="top"
                    menuPortalTarget={document.getElementById("root")}
                    // @ts-ignore
                    error={errors?.["font_family"]?.message}
                    options={fontFamilyOptions}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    label={t(`fontFamily`)}
                    value={fontFamilyOptions?.find(
                      (option) => props?.value === option.value
                    )}
                    onChange={(e) => {
                      props.onChange(e.value);
                    }}
                  ></Select>
                )}
              />
            </div>
          </Col>
        </Row>

        <div className="my-8 flex justify-between">
          <Controller
            name={`show_address`}
            defaultValue={true}
            control={control}
            rules={{
              required: false,
            }}
            render={(props) => (
              <BooleanWithLabel
                checked={props.value}
                onChange={(e) => props.onChange(e.target.checked)}
                labelAtTop
              >
                {t("show_address")}
              </BooleanWithLabel>
            )}
          />
          <Controller
            name={`show_public_order_number`}
            defaultValue={true}
            control={control}
            rules={{
              required: false,
            }}
            render={(props) => (
              <BooleanWithLabel
                checked={props.value}
                onChange={(e) => props.onChange(e.target.checked)}
                labelAtTop
              >
                {t("showPublicOrderNumber")}
              </BooleanWithLabel>
            )}
          />
          <Controller name={`show_price`} defaultValue={true} control={control} rules={{ required: false }} render={(props) => (
            <BooleanWithLabel checked={props.value} onChange={(e) => props.onChange(e.target.checked)} labelAtTop>
              {t("showPrice")}
            </BooleanWithLabel>
          )} />
          <Controller
            name={`show_category_bracket`}
            defaultValue={true}
            control={control}
            rules={{
              required: false,
            }}
            render={(props) => (
              <BooleanWithLabel
                checked={props.value}
                onChange={(e) => props.onChange(e.target.checked)}
                labelAtTop
              >
                {t("showCategory")}
              </BooleanWithLabel>
            )}
          />
          <Controller
            name={`apply_on_menu`}
            defaultValue={true}
            control={control}
            rules={{
              required: false,
            }}
            render={(props) => (
              <BooleanWithLabel
                checked={props.value}
                onChange={(e) => props.onChange(e.target.checked)}
                labelAtTop
              >
                {t("discounts.applyOnMenuLabel")}
              </BooleanWithLabel>
            )}
          />
        </div>

        <Controller
          defaultValue={undefined}
          key={name}
          name={name}
          control={control}
          rules={{
            required: {
              value: isAppliedOnWholeMenu === false,
              message: t("fieldRequired"),
            },
          }}
          render={(props) => (
            <div className="my-[2rem]">
              <Select
                isMulti
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                menuPlacement="top"
                menuPortalTarget={document.getElementById("root")}
                // @ts-ignore
                error={errors?.[name]?.message}
                options={query.categories ?? []}
                getOptionLabel={(option) => getFieldByLang(option.name)}
                getOptionValue={(option) => option.item_id}
                label={t(
                  `discounts.${
                    isAppliedOnWholeMenu ? "exceptions" : "force"
                  }OnCategoriesLabel`
                )}
                value={query?.categories?.filter((option) =>
                  props?.value?.includes(option.item_id)
                )}
                onChange={(e) => {
                  let value = e || [];
                  props.onChange(value.map((v) => v.item_id));
                }}
              ></Select>
            </div>
          )}
        />
        <Controller
          defaultValue={1}
          name={"language"}
          control={control}
          rules={{
            required: {
              value: true,
              message: t("fieldRequired"),
            },
          }}
          render={(props) => (
            <div className="my-[2rem]">
              <Select
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                menuPlacement="top"
                menuPortalTarget={document.getElementById("root")}
                // @ts-ignore
                error={errors?.["language"]?.message}
                options={countryLanguages}
                getOptionLabel={(option) =>
                  t(`${getFieldByLang(option.label)}`)
                }
                getOptionValue={(option) => option.value}
                label={t(`cms_users.language`)}
                value={countryLanguages.find((l) => l.value === props.value)}
                onChange={(v) => props.onChange(v.value)}
              ></Select>
            </div>
          )}
        />
        <Button isPending={create.isPending} type="submit">
          {t("save")}
        </Button>
      </form>
    </Modal>
  );
};

export default ScreenModal;
